import { useInfiniteQuery } from '@tanstack/react-query'
import { fetcher } from '../../fetcher'
import { getLibraryListQuery } from '../../graphql/LibrariesQuery'
import { useConfigStore } from '../../store/ConfigStore'
import { useLibraryStore } from '../../store/LibraryStore'
import { Provider } from '../provider'
import LibraryItem from './LibraryItem'
import { useMemo, useRef, useEffect } from 'react'
import '../../instrument'
import { useShallow } from 'zustand/shallow'

const INITIAL_PAGE = 0
const PAGE_SIZE = 50

function RasterLibraries() {
  const { config } = useConfigStore()
  const [setName, setId, id, setImageCount] = useLibraryStore(
    useShallow((state) => [state.setName, state.setId, state.id, state.setImageCount]),
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['raster-libraries'],
    initialPageParam: INITIAL_PAGE,
    queryFn: ({ pageParam = INITIAL_PAGE }) => {
      return fetcher(
        getLibraryListQuery,
        {
          organizationId: config.orgId!,
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
        {
          apiKey: config.apiKey,
          orgId: config.orgId,
        },
      )
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.libraries?.length || lastPage.libraries.length < PAGE_SIZE) {
        return undefined
      }
      return allPages.length
    },
    enabled: !!config.orgId && !!config.apiKey,
  })

  // Combine all pages of data
  const allLibraries = useMemo(() => {
    return data?.pages.reduce((acc, page) => {
      return [...acc, ...(page?.libraries || [])]
    }, [] as Library[])
  }, [data?.pages])

  // Intersection Observer setup for infinite scroll
  const observerTarget = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 },
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  return (
    <div className="flex flex-col gap-2 w-full sm:max-w-56 shrink-0">
      {allLibraries?.map((library: Library) => (
        <LibraryItem
          key={library.id}
          library={library}
          id={id}
          setName={setName}
          setId={setId}
          setImageCount={setImageCount}
        />
      ))}

      {/* Loading indicator and observer target */}
      <div ref={observerTarget} className="h-4 w-full">
        {isFetchingNextPage && <div className="w-full h-10 animate-pulse bg-gray-200 rounded-md" />}
      </div>
    </div>
  )
}

export function Libraries({ config }: { config: Settings }) {
  return (
    <Provider config={config}>
      <RasterLibraries />
    </Provider>
  )
}
